import {
  Box,
  Card,
  CardBody,
  Heading,
  HStack,
  Progress,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import type { StoreGroupReportFlow } from 'services/interfaces/storeGroupReport';
import { formatCurrency } from './reportFormat';

interface ReportFlowPanelProps {
  flow: StoreGroupReportFlow;
}

// Mesmas cores do donut do ionic (store-flow-panel.component.ts).
const SAME_STORE_COLOR = '#19a1e6';
const OTHER_STORE_COLOR = '#f45b93';

// Réplica do store-flow-panel do ionic: donut (recharts) + barras de fluxo (Chakra Progress).
const ReportFlowPanel: React.FC<ReportFlowPanelProps> = ({ flow }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const total = flow.flow.same_store + flow.flow.other_store;
  const balanceRows = flow.balance.filter((row) => row.idi);

  const sameStorePct =
    total > 0 ? Math.round((flow.flow.same_store / total) * 100) : 0;
  const otherStorePct =
    total > 0 ? Math.round((flow.flow.other_store / total) * 100) : 0;
  const activeClients = flow.flow.active_clients;
  const multiStorePct =
    activeClients > 0
      ? Math.round((flow.flow.multi_store_clients / activeClients) * 100)
      : 0;

  const donutData = [
    { name: 'same', value: flow.flow.same_store, color: SAME_STORE_COLOR },
    { name: 'other', value: flow.flow.other_store, color: OTHER_STORE_COLOR },
  ];

  const flowBars = [
    {
      label: 'Resgate na mesma loja',
      pct: sameStorePct,
      color: SAME_STORE_COLOR,
    },
    {
      label: 'Resgate em outra loja',
      pct: otherStorePct,
      color: OTHER_STORE_COLOR,
    },
    {
      label: 'Clientes multi-loja',
      pct: multiStorePct,
      color: 'purple.400',
    },
  ];

  return (
    <Stack spacing={4}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Stack spacing={3} justify="center">
          {flowBars.map((bar) => (
            <Box key={bar.label}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="sm" color="gray.600" fontWeight="bold">
                  {bar.label}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {bar.pct}%
                </Text>
              </HStack>
              <Progress
                value={bar.pct}
                size="sm"
                borderRadius="full"
                sx={{ '& > div': { background: bar.color } }}
              />
            </Box>
          ))}
        </Stack>

        <Box position="relative" h="180px">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={donutData}
                dataKey="value"
                innerRadius="65%"
                outerRadius="100%"
                startAngle={90}
                endAngle={-270}
                stroke="none"
              >
                {donutData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <Stack
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            spacing={0}
            align="center"
          >
            <Text fontSize="lg" fontWeight="bold">
              {formatCurrency(total)}
            </Text>
            <Text fontSize="xs" color="gray.500">
              total resgatado
            </Text>
          </Stack>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Card bg={cardBg}>
          <CardBody py={3} px={4}>
            <Stat size="sm">
              <StatLabel color="gray.600" fontWeight="bold">
                Clientes multi-loja
              </StatLabel>
              <StatNumber fontSize="lg">
                {flow.flow.multi_store_clients}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={cardBg}>
          <CardBody py={3} px={4}>
            <Stat size="sm">
              <StatLabel color="gray.600" fontWeight="bold">
                Clientes ativos
              </StatLabel>
              <StatNumber fontSize="lg">{flow.flow.active_clients}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {balanceRows.length > 0 && (
        <TableContainer>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Loja</Th>
                <Th isNumeric>Gerado</Th>
                <Th isNumeric>Resgate interno</Th>
                <Th isNumeric>Recebido de outras</Th>
                <Th isNumeric>Enviado a outras</Th>
              </Tr>
            </Thead>
            <Tbody>
              {balanceRows.map((row) => (
                <Tr key={row.idi}>
                  <Td>{row.name || `Loja ${row.idi}`}</Td>
                  <Td isNumeric>{formatCurrency(row.generated)}</Td>
                  <Td isNumeric>{formatCurrency(row.internal_redeem)}</Td>
                  <Td isNumeric>{formatCurrency(row.received_from_others)}</Td>
                  <Td isNumeric>{formatCurrency(row.sent_to_others)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {flow.routes.length > 0 && (
        <Stack spacing={2}>
          <Heading size="sm" color="gray.600">
            Principais rotas de resgate
          </Heading>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>De</Th>
                  <Th>Para</Th>
                  <Th isNumeric>Valor</Th>
                  <Th isNumeric>Resgates</Th>
                </Tr>
              </Thead>
              <Tbody>
                {flow.routes.map((route) => (
                  <Tr key={`${route.from_idi}-${route.to_idi}`}>
                    <Td>{route.from}</Td>
                    <Td>{route.to}</Td>
                    <Td isNumeric>{formatCurrency(route.value)}</Td>
                    <Td isNumeric>{route.count}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Stack>
  );
};

export default ReportFlowPanel;
